import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

// ── Action Menu ─────────────────────────────────────────────────────────────
const ActionMenu = ({ profile, onToggleStatus, onApiKey, onClientSecret }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleToggle = () => {
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const menuHeight = 130;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight
                ? rect.top - menuHeight + window.scrollY
                : rect.bottom + window.scrollY;
            setMenuPos({ top, left: rect.right - 195 });
        }
        setOpen(!open);
    };

    const menuItems = [
        {
            icon: profile.status ? 'bi-person-x' : 'bi-person-check',
            label: profile.status ? 'Deactivate Profile' : 'Activate Profile',
            color: profile.status ? '#dc2626' : '#16a34a',
            onClick: () => { onToggleStatus(profile._id, profile.status); setOpen(false); },
        },
        {
            icon: 'bi-key',
            label: profile.api_key ? 'View API Key' : 'Add API Key',
            color: '#1565c0',
            onClick: () => { onApiKey(profile._id, profile.api_key); setOpen(false); },
        },
        {
            icon: 'bi-shield-lock',
            label: 'Client Secret Key',
            color: '#7c3aed',
            onClick: () => { onClientSecret(profile._id, profile.client_secret); setOpen(false); },
        },
    ];

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button style={menuStyles.trigger} onClick={handleToggle}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <div style={{ ...menuStyles.menu, position: 'fixed', top: menuPos.top, left: menuPos.left }}>
                    {menuItems.map((item, i) => (
                        <button key={i} style={menuStyles.item} onClick={item.onClick}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                            <span style={{ ...menuStyles.itemIcon, background: item.color + '15', color: item.color }}>
                                <i className={`bi ${item.icon}`}></i>
                            </span>
                            <span style={{ ...menuStyles.itemLabel, color: item.color }}>{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const menuStyles = {
    trigger: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', fontSize: '15px' },
    menu: { position: 'absolute', right: 0, top: '36px', background: 'white', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', minWidth: '190px', zIndex: 1000, padding: '6px' },
    item: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '8px 10px', border: 'none', background: 'white', borderRadius: '6px', cursor: 'pointer', textAlign: 'left' },
    itemIcon: { width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 },
    itemLabel: { fontSize: '13px', fontWeight: '500' },
};

// ── Main Component ───────────────────────────────────────────────────────────
const WhatsappProfileComponent = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showApiKeyModal, setShowApiKeyModal] = useState(false);
    const [showClientSecretModal, setShowClientSecretModal] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [apiKey, setApiKey] = useState('');
    const [apiKeyFlag, setApiKeyFlag] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    const [apiKeyError, setApiKeyError] = useState('');
    const itemsPerPage = 5;

    const fetchProfiles = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/profiles`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to fetch profiles');
            const data = await response.json();
            setProfiles(data);
        } catch (err) {
            console.error('Error fetching profiles:', err);
            setError('Failed to load profiles. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

    const filteredProfiles = profiles.filter(
        (profile) =>
            String(profile.whatsapp_no).includes(searchTerm) ||
            profile.enterprise_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentProfiles = filteredProfiles.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage);

    const openApiKeyModal = (profileId, existingApiKey) => {
        setApiKeyFlag(!!existingApiKey);
        setSelectedProfileId(profileId);
        setApiKey(existingApiKey || '');
        setApiKeyError('');
        setShowApiKeyModal(true);
    };

    const openClientSecretModal = (profileId, secret) => {
        setSelectedProfileId(profileId);
        setClientSecret(secret);
        setShowClientSecretModal(true);
    };

    const handleToggleStatus = async (profileId, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/profilestatusUpdate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ profileId }),
            });
            if (response.ok) {
                setProfiles(profiles.map((p) =>
                    p._id === profileId ? { ...p, status: !currentStatus } : p
                ));
                toast.success(`Profile ${currentStatus ? 'deactivated' : 'activated'} successfully!`, { autoClose: 3000 });
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to update status.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    const handleAddApiKey = async (profileId) => {
        if (!apiKey.trim()) { setApiKeyError('Please enter an API key.'); return; }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/profiles/${profileId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ api_key: apiKey }),
            });
            if (response.ok) {
                setProfiles(profiles.map((p) =>
                    p._id === profileId ? { ...p, api_key: apiKey } : p
                ));
                toast.success('API Key added successfully!', { autoClose: 3000 });
                setShowApiKeyModal(false);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to update API Key.', { autoClose: 3000 });
                setApiKeyError(errorData.error || 'Failed to update API Key.');
            }
        } catch (error) {
            console.error('Error updating API key:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    const handleTokenRefresh = async (profileId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/profileTokenRefresh`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ profileId }),
            });
            if (response.ok) {
                const data = await response.json();
                setClientSecret(data.client_secret);
                toast.success(data.message, { autoClose: 3000 });
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to refresh token.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>WhatsApp Profiles</h5>
                        <p style={styles.pageSubtitle}>Manage WhatsApp business profiles</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={() => navigate('/createwhatsappprofile')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Add Profile
                    </button>
                </div>

                {/* Search */}
                <div style={styles.searchRow}>
                    <div style={styles.searchWrapper}>
                        <i className="bi bi-search" style={styles.searchIcon}></i>
                        <input
                            type="text"
                            placeholder="Search by WhatsApp number or enterprise name..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            style={styles.searchInput}
                        />
                    </div>
                    <span style={styles.resultCount}>{filteredProfiles.length} profile{filteredProfiles.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Table card */}
                <div style={styles.card}>
                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p style={styles.stateText}>Loading profiles…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchProfiles}>Retry</button>
                        </div>
                    ) : filteredProfiles.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-whatsapp" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>{searchTerm ? 'No profiles match your search.' : 'No WhatsApp profiles found. Add your first profile.'}</p>
                            {!searchTerm && <button style={styles.btnPrimary} onClick={() => navigate('/createwhatsappprofile')}>Add Profile</button>}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>WhatsApp No</th>
                                        <th style={styles.th}>Country</th>
                                        <th style={styles.th}>Enterprise Name</th>
                                        <th style={styles.th}>Assigned To</th>
                                        <th style={styles.th}>Created By</th>
                                        <th style={styles.th}>Website</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Created On</th>
                                        <th style={styles.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentProfiles.map((profile) => (
                                        <tr key={profile._id} style={styles.tr}>
                                            <td style={styles.td}>{profile.whatsapp_no}</td>
                                            <td style={styles.td}>{profile.country}</td>
                                            <td style={styles.td}>{profile.enterprise_name}</td>
                                            <td style={styles.td}>
                                                <span style={styles.ownerBadge}>
                                                    <i className="bi bi-person" style={{ marginRight: 4 }}></i>
                                                    {profile.owner_name || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={styles.createdByBadge}>
                                                    <i className="bi bi-person-badge" style={{ marginRight: 4 }}></i>
                                                    {profile.created_by_name || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={{ ...styles.td, maxWidth: '160px', wordBreak: 'break-all' }}>
                                                <a href={profile.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', fontSize: '13px' }}>
                                                    {profile.website_url}
                                                </a>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{ ...styles.badge, ...(profile.status ? styles.badgeActive : styles.badgeInactive) }}>
                                                    {profile.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{profile.createdAt}</td>
                                            <td style={styles.td}>
                                                <ActionMenu
                                                    profile={profile}
                                                    onToggleStatus={handleToggleStatus}
                                                    onApiKey={openApiKeyModal}
                                                    onClientSecret={openClientSecretModal}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!isLoading && !error && filteredProfiles.length > 0 && (
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                )}

                <ToastContainer />

                {/* API Key Modal */}
                {showApiKeyModal && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalBox}>
                            <div style={styles.modalHeader}>
                                <h6 style={styles.modalTitle}>{apiKeyFlag ? 'View API Key' : 'Add API Key'}</h6>
                                <button style={styles.closeBtn} onClick={() => setShowApiKeyModal(false)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div style={styles.modalBody}>
                                <label style={styles.label}>{apiKeyFlag ? 'Your API Key' : 'Enter API Key'}</label>
                                {apiKeyFlag ? (
                                    <div style={styles.copyRow}>
                                        <span style={styles.copyValue}>{apiKey}</span>
                                        <button style={styles.copyBtn} onClick={() => {
                                            navigator.clipboard.writeText(apiKey);
                                            toast.success('API Key copied!', { autoClose: 2000 });
                                        }}>
                                            <i className="bi bi-clipboard"></i>
                                        </button>
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        value={apiKey}
                                        placeholder="Paste your API key here"
                                        style={{ ...styles.input, ...(apiKeyError ? styles.inputError : {}) }}
                                        onChange={(e) => { setApiKey(e.target.value); setApiKeyError(''); }}
                                    />
                                )}
                                {apiKeyError && <span style={styles.fieldError}>{apiKeyError}</span>}
                            </div>
                            <div style={styles.modalFooter}>
                                <button style={styles.btnSecondary} onClick={() => setShowApiKeyModal(false)}>Cancel</button>
                                {!apiKeyFlag && (
                                    <button style={styles.btnPrimary} onClick={() => handleAddApiKey(selectedProfileId)}>Save API Key</button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Client Secret Modal */}
                {showClientSecretModal && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalBox}>
                            <div style={styles.modalHeader}>
                                <h6 style={styles.modalTitle}>Client Secret Key</h6>
                                <button style={styles.closeBtn} onClick={() => setShowClientSecretModal(false)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div style={styles.modalBody}>
                                <label style={styles.label}>Secret Key</label>
                                <div style={styles.copyRow}>
                                    <span style={{ ...styles.copyValue, wordBreak: 'break-all', fontSize: '12px' }}>{clientSecret}</span>
                                    <button style={styles.copyBtn} onClick={() => {
                                        navigator.clipboard.writeText(clientSecret);
                                        toast.success('Secret key copied!', { autoClose: 2000 });
                                    }}>
                                        <i className="bi bi-clipboard"></i>
                                    </button>
                                </div>
                            </div>
                            <div style={styles.modalFooter}>
                                <button style={styles.btnSecondary} onClick={() => setShowClientSecretModal(false)}>Close</button>
                                <button style={styles.btnPrimary} onClick={() => handleTokenRefresh(selectedProfileId)}>
                                    <i className="bi bi-arrow-clockwise" style={{ marginRight: 6 }}></i>
                                    Refresh Token
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    searchRow: { display: 'flex', alignItems: 'center', gap: '12px' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: '400px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' },
    searchInput: { width: '100%', padding: '9px 12px 9px 36px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
    resultCount: { fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', borderBottom: '2px solid #bfdbfe' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeActive: { background: '#dcfce7', color: '#16a34a' },
    badgeInactive: { background: '#fee2e2', color: '#dc2626' },
    ownerBadge: { display: 'inline-flex', alignItems: 'center', background: '#eff6ff', color: '#1565c0', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
    createdByBadge: { display: 'inline-flex', alignItems: 'center', background: '#f5f3ff', color: '#7c3aed', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modalBox: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden' },
    modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    modalTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0 },
    modalBody: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #f3f4f6' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    copyRow: { display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px' },
    copyValue: { flex: 1, fontSize: '13px', color: '#374151', wordBreak: 'break-all' },
    copyBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#1565c0', fontSize: '16px', padding: 0, flexShrink: 0 },
};

export default WhatsappProfileComponent;
